import React, { useState, useRef, useEffect } from 'react';
import { useSearchRefresh } from '../context/SearchRefreshContext';
import './ThreeDUpload.css';
import {
  VStack,
  Card,
  CardBody,
  CardHeader,
  Heading,
  Text,
  useColorModeValue,
  useColorMode,
  useToast,
  Flex,
  useBreakpointValue
} from '@chakra-ui/react';
import { Cube } from '@phosphor-icons/react';
import SubLocationSelector from './media/SubLocationSelector';
import { uploadWithProgress } from '../utils/upload';
import { auth } from '../firebase/config';
import { getDoc, doc } from 'firebase/firestore';
import { db } from '../firebase/config';
import { addSiteMedia } from '../firebase/siteMedia';
import ThreeDUploadActions from './media/ThreeDUploadActions';
import MediaUploadProgress from './media/MediaUploadProgress';
import ThreeDPreviewSection from './media/ThreeDPreviewSection';

const ThreeDUpload = ({ onUploadComplete, siteId, media, isDisabled, isMobile }) => {
  const { triggerDelayedRefresh } = useSearchRefresh();
  const toast = useToast();
  const { colorMode } = useColorMode();
  const fileInputRef = useRef(null);
  const [modelSrc, setModelSrc] = useState(null); // Blob URL for preview
  const [modelFile, setModelFile] = useState(null);
  const [modelInfo, setModelInfo] = useState(null); // File info (size, type, etc.)
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [currentStep, setCurrentStep] = useState('idle');
  const [isUploaded, setIsUploaded] = useState(false);
  const [selectedSubLocationId, setSelectedSubLocationId] = useState(null);

  // Responsive layout
  const iconSize = useBreakpointValue({ base: 18, md: 22 });
  const iconWeight = useBreakpointValue({ base: "regular", md: "light" });

  // Theme colors using semantic tokens
  const cardBg = useColorModeValue('bg.surface', 'bg.surface');
  const borderColor = useColorModeValue('border.default', 'border.default');
  const accentColor = useColorModeValue('brand.primary', 'brand.primary');
  const textColor = useColorModeValue('text.muted', 'text.muted');
  const headingColor = useColorModeValue('text.default', 'text.default');
  const toastSuccessBorder = useColorModeValue('success', 'success');
  const toastErrorBorder = useColorModeValue('error', 'error');
  const toastWarningBorder = useColorModeValue('warning', 'warning');
  const toastBg = useColorModeValue('bg.surface', 'bg.surface');

  // Supported 3D file extensions
  const SUPPORTED_3D_EXTENSIONS = ['ply', 'obj', 'stl', 'gltf', 'glb', 'ifc', 'las', 'laz'];

  // Reset function
  const resetState = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    if (modelSrc && modelSrc.startsWith('blob:')) {
      URL.revokeObjectURL(modelSrc);
    }
    setModelFile(null);
    setModelSrc(null);
    setModelInfo(null);
    setUploadProgress(0);
    setError(null);
    setCurrentStep('idle');
    setIsUploaded(false);
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Get file extension
      const extension = file.name.split('.').pop().toLowerCase();
      
      // Check if it's a supported 3D file
      if (!SUPPORTED_3D_EXTENSIONS.includes(extension)) {
        toast({
          title: "Invalid file type",
          description: `Please select a 3D file. Supported formats: ${SUPPORTED_3D_EXTENSIONS.join(', ')}`,
          status: "error",
          duration: 5000,
          variant: "subtle",
          containerStyle: { borderWidth: '1px', borderColor: toastErrorBorder, bg: toastBg },
        });
        return;
      }
      
      // Check file size (100MB limit for 3D files)
      if (file.size > 100 * 1024 * 1024) {
        toast({
          title: "File too large",
          description: "Please select a 3D file under 100MB",
          status: "error",
          duration: 5000,
          variant: "subtle",
          containerStyle: { borderWidth: '1px', borderColor: toastErrorBorder, bg: toastBg },
        });
        return;
      }

      // Reset state before processing new file
      resetState();

      // Set the new file
      setModelFile(file);
      
      // Create blob URL for 3D viewer
      const blobUrl = URL.createObjectURL(file);
      setModelSrc(blobUrl);

      // Set model info
      setModelInfo({
        name: file.name,
        size: file.size,
        type: extension.toUpperCase(),
        lastModified: new Date(file.lastModified)
      });

      toast({
        title: "3D File Selected",
        description: `${file.name} ready for preview and upload`,
        status: "success",
        duration: 3000,
        variant: "subtle",
        containerStyle: { borderWidth: '1px', borderColor: toastSuccessBorder, bg: toastBg },
      });
    }
  };



  const handleUpload = async () => {
    if (!modelFile || isUploaded) return;
    setIsLoading(true);
    setCurrentStep('uploading');
    
    try {
      let selectedSubLocation = null;
      if (selectedSubLocationId) {
        try {
          const subLocationDoc = await getDoc(doc(db, 'site_sublocations', selectedSubLocationId));
          if (subLocationDoc.exists()) {
            selectedSubLocation = { id: subLocationDoc.id, ...subLocationDoc.data() };
          }
        } catch (error) {
          console.error('Error fetching sublocation:', error);
        }
      }

      const isFirstUpload = !media || media.length === 0;
      const metadata = {
        sublocationId: selectedSubLocationId,
        coordinates: selectedSubLocation?.coordinates || null,
        isMain: isFirstUpload,
        fileType: '3d', // Explicitly set file type
        modelInfo: modelInfo,
        predictions: [], // 3D files don't have predictions
        features: {} // 3D files don't have features
      };

      console.log('ThreeDUpload - Upload metadata:', metadata);

      let uploadedFile;
      try {
        // Try R2 upload first
        uploadedFile = await uploadWithProgress(
          modelFile, 
          siteId, 
          metadata, 
          (progress) => setUploadProgress(progress)
        );
        
        toast({
          title: "Upload Complete",
          description: "3D model uploaded successfully to R2 storage",
          status: "success",
          duration: 3000,
          variant: "subtle",
          containerStyle: { borderWidth: '1px', borderColor: toastSuccessBorder, bg: toastBg },
        });
      } catch (error) {
        console.log('R2 upload failed:', error.message);
        throw error; // Re-throw the error to be handled by the outer catch block
      }

      // Update modelSrc with the permanent URL
      const finalUrl = uploadedFile.url;
      if (modelSrc && modelSrc.startsWith('blob:')) {
        URL.revokeObjectURL(modelSrc); // Clean up blob URL
      }
      setModelSrc(finalUrl);

      onUploadComplete([{
        url: finalUrl || '',
        path: uploadedFile.path || '',
        type: uploadedFile.type || '3d',
        name: uploadedFile.name || '',
        size: uploadedFile.size || 0,
        uploadedBy: uploadedFile.uploadedBy || 'anonymous',
        sublocationId: selectedSubLocationId,
        coordinates: metadata.coordinates,
        features: {},
        predictions: [],
        isMain: metadata.isMain,
        uploadedAt: uploadedFile.uploadedAt || new Date().toISOString(),
        modelInfo: modelInfo
      }]);

      // Use delayed refresh to allow backend sync to complete
      triggerDelayedRefresh(3000);
      setIsUploaded(true);
      
    } catch (error) {
      console.error('Error uploading 3D file:', error);
      setError(error.message || 'Error uploading 3D file. Please try again.');
      
      toast({
        title: "Upload Failed",
        description: error.message || "R2 upload failed. Please try again.",
        status: "error",
        duration: 5000,
        variant: "subtle",
        containerStyle: { borderWidth: '1px', borderColor: toastErrorBorder, bg: toastBg },
      });
    } finally {
      setIsLoading(false);
      setCurrentStep('idle');
    }
  };

  // Clean up blob URL on unmount
  useEffect(() => {
    return () => {
      if (modelSrc && modelSrc.startsWith('blob:')) {
        URL.revokeObjectURL(modelSrc);
      }
    };
  }, [modelSrc]);

  return (
    <Card
      variant="parchment"
      border="1px"
      borderColor={borderColor}
      borderRadius="lg"
      boxShadow="sm"
      overflow="hidden"
      transition="all 0.2s"
      _hover={{ boxShadow: "md" }}
      p={isMobile ? 2 : 4} /* Reduce padding on mobile */
    >
      <CardHeader pb={isMobile ? 1 : 2}>
        <Flex align="center" gap={2} mb={isMobile ? 1 : 2}>
          <Cube size={iconSize} weight={iconWeight} color={accentColor} />
          <Heading size={isMobile ? "sm" : "md"} textStyle="carved" color={headingColor}>
            Upload 3D Models
          </Heading>
        </Flex>
        <Text color={textColor} fontSize={isMobile ? "xs" : "sm"} textStyle="papyrus">
          Upload 3D models, point clouds, and scans for interactive viewing
        </Text>
      </CardHeader>

      <CardBody>
        <VStack spacing={isMobile ? 4 : 6} align="stretch">
          <ThreeDUploadActions
            fileInputRef={fileInputRef}
            modelFile={modelFile}
            isLoading={isLoading}
            isUploaded={isUploaded}
            onSelectClick={() => fileInputRef.current?.click()}
            onUploadClick={handleUpload}
            siteId={siteId}
            selectedSubLocationId={selectedSubLocationId}
            onSubLocationSelect={(selection) => setSelectedSubLocationId(selection.subLocationId)}
            accentColor={accentColor}
            isMobile={isMobile}
          />

          <input
            ref={fileInputRef}
            type="file"
            accept=".ply,.obj,.stl,.gltf,.glb,.ifc,.las,.laz"
            onChange={handleFileChange}
            style={{ display: 'none' }}
          />

          <MediaUploadProgress
            currentStep={currentStep}
            uploadProgress={uploadProgress}
            error={error}
            fileType="3d"
          />

          <ThreeDPreviewSection
            modelSrc={modelSrc}
            modelInfo={modelInfo}
            isUploaded={isUploaded}
            accentColor={accentColor}
            isLoading={isLoading}
            isMobile={isMobile}
          />
        </VStack>
      </CardBody>
    </Card>
  );
};

export default ThreeDUpload;
